import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchAdminMe, adminLogout, installAdminAuthGuard, ADMIN_LOGOUT_EVENT, AdminUser } from '../../lib/adminAuth';
import LoginPage from '../../pages/admin/LoginPage';
import { AdminI18nProvider, AdminLangSelect, useAdminI18n } from '../../lib/adminI18n';
import './AdminLayout.css';

function AdminLayout() {
  return <AdminI18nProvider><AdminLayoutInner /></AdminI18nProvider>;
}

function AdminLayoutInner() {
  const { t } = useTranslation();
  const { at, lang } = useAdminI18n();
  const location = useLocation();
  const [loggedIn, setLoggedIn] = useState(false);
  const [me, setMe] = useState<AdminUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  // 左上のタイトル（設定 > 基本設定「管理画面のタイトル」。空なら既定のサイト名）
  const [adminTitle, setAdminTitle] = useState('');
  useEffect(() => {
    const onChange = (e: Event) => setAdminTitle(String((e as CustomEvent).detail || ''));
    window.addEventListener('admin-title-changed', onChange);
    return () => window.removeEventListener('admin-title-changed', onChange);
  }, []);
  useEffect(() => {
    if (!loggedIn) return;
    fetch('/api/admin/settings', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setAdminTitle(String(d?.settings?.admin_title || '')))
      .catch(() => {});
  }, [loggedIn]);

  useEffect(() => {
    installAdminAuthGuard();
    const onLogout = () => { setLoggedIn(false); setMe(null); };
    window.addEventListener(ADMIN_LOGOUT_EVENT, onLogout);
    // 起動時に Cookie セッションの有効性をサーバーへ確認
    (async () => {
      const u = await fetchAdminMe();
      setMe(u);
      setLoggedIn(!!u);
      setChecking(false);
    })();
    return () => window.removeEventListener(ADMIN_LOGOUT_EVENT, onLogout);
  }, []);

  // ルート遷移のたびにモバイルドロワーを閉じる
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (checking) {
    return <div className="admin-login"><div className="admin-login-card">{at('loading')}</div></div>;
  }

  const menuItems = [
    { path: '/admin', label: at('nav.dashboard'), exact: true },
    { path: '/admin/calendar', label: at('nav.calendar') },
    { path: '/admin/cleaning', label: at('nav.cleaning') },
    { path: '/admin/reservations', label: at('nav.reservations') },
    { path: '/admin/messages', label: at('nav.messages') },
    { path: '/admin/properties', label: at('nav.properties') },
    { path: '/admin/passport', label: at('nav.passport') },
    { path: '/admin/checkin', label: at('nav.checkin') },
    { path: '/admin/settings', label: at('nav.settings') },
    { path: '/admin/admin-users', label: at('nav.adminUsers') },
  ];

  if (!loggedIn) {
    return <LoginPage onLoggedIn={() => { void fetchAdminMe().then((u) => { setMe(u); setLoggedIn(!!u); }); }} />;
  }

  return (
    <div className="admin-layout">
      <header className="admin-topbar">
        <button
          type="button"
          className="admin-menu-toggle"
          aria-label={at('menu')}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="admin-menu-toggle-bar" />
          <span className="admin-menu-toggle-bar" />
          <span className="admin-menu-toggle-bar" />
        </button>
        <div className="admin-topbar-title">{adminTitle || t('common.siteName')}</div>
      </header>
      {menuOpen && <div className="admin-backdrop" onClick={() => setMenuOpen(false)} />}
      <aside className={`admin-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="admin-logo">
          <Link to="/admin">{adminTitle || t('common.siteName')}</Link>
        </div>
        <nav className="admin-nav">
          {menuItems.map((item) => {
            // パス境界で判定する。startsWith だけだと /admin/checkin が
            // /admin/checkin-device まで一致してしまうため、完全一致または
            // 直後が '/' の場合のみ active にする。
            const active = item.exact
              ? location.pathname === item.path
              : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-item ${active ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="admin-footer">
          <AdminLangSelect />
          {me && <span className="admin-user-name" title={me.login_id || at('legacyUser')}>{me.name}</span>}
          <button type="button" className="admin-logout" onClick={() => { void adminLogout(); }}>
            {at('logout')}
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <Outlet key={lang} />
      </main>
    </div>
  );
}

export default AdminLayout;
